import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

import { orderService } from '../../services/orderService';

// Order detail interfaces
interface OrderHeader {
  orderNumber: string;
  orderDate: string;
  customerCode: string;
  customerName: string;
  customerAddress: string;
  totalAmount: number;
  currency: string;
  status: string;
  orderType: string;
  description?: string;
  createdBy: string;
  createdDate: string;
  lastUpdatedBy?: string;
  lastUpdatedDate?: string;
}

interface OrderLine {
  lineNumber: number;
  itemCode: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  unit: string;
  description?: string;
}

interface OrderDetailData {
  header: OrderHeader;
  lines: OrderLine[];
}

const OrderDetailPage: React.FC = () => {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  
  const orderType = searchParams.get('type') || 'wholesale';
  
  // State definitions
  const [orderDetail, setOrderDetail] = useState<OrderDetailData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Fetch order detail
  const fetchOrderDetail = async () => {
    if (!orderNumber) return;
    
    setLoading(true);
    try {
      const response = await orderService.getOrderDetail(orderNumber);
      setOrderDetail(response);
    } catch (error) {
      console.error('Error fetching order detail:', error);
      enqueueSnackbar('Sipariş detayı yüklenirken hata oluştu', { variant: 'error' });
      navigate(`/orders/list?type=${orderType}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderDetail();
  }, [orderNumber]);

  // Navigation functions
  const handleBack = () => {
    navigate(`/orders/list?type=${orderType}`);
  };

  const handleEdit = () => {
    navigate(`/orders/edit/${orderNumber}?type=${orderType}`);
  };

  // Format functions
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currency || 'TRY'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('tr-TR');
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending':
      case 'bekliyor':
        return 'warning';
      case 'approved':
      case 'onaylandi':
        return 'success';
      case 'cancelled':
      case 'iptal':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!orderDetail) {
    return (
      <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
        <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            Sipariş bulunamadı
          </Typography>
          <Button onClick={handleBack} sx={{ mt: 2 }}>
            Geri Dön
          </Button>
        </Paper>
      </Container>
    );
  }

  const { header, lines } = orderDetail;

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
          Sipariş Detayı - {header.orderNumber}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
          >
            Düzenle
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
          >
            Yazdır
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
          >
            İndir
          </Button>
        </Box>
      </Box>

      {/* Order Header Information */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ borderBottom: '1px solid #eee', pb: 1, mb: 2 }}>
          Sipariş Bilgileri
        </Typography>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Sipariş Numarası
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {header.orderNumber}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Sipariş Tarihi
                </Typography>
                <Typography variant="body1">
                  {formatDate(header.orderDate)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Müşteri Kodu
                </Typography>
                <Typography variant="body1">
                  {header.customerCode}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Durum
                </Typography>
                <Chip
                  label={header.status}
                  color={getStatusColor(header.status) as any}
                  size="small"
                />
              </Box>
            </Box>
          </Box>
          
          <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Müşteri Adı
                </Typography>
                <Typography variant="body1">
                  {header.customerName}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Adres
                </Typography>
                <Typography variant="body2">
                  {header.customerAddress}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Toplam Tutar
                </Typography>
                <Typography variant="h6" color="primary">
                  {formatCurrency(header.totalAmount, header.currency)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        
        {header.description && (
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #eee' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Açıklama
            </Typography>
            <Typography variant="body2">
              {header.description}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Order Lines */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ borderBottom: '1px solid #eee', pb: 1, mb: 2 }}>
          Sipariş Kalemleri
        </Typography>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sıra</TableCell>
                <TableCell>Ürün Kodu</TableCell>
                <TableCell>Ürün Adı</TableCell>
                <TableCell align="right">Miktar</TableCell>
                <TableCell>Birim</TableCell>
                <TableCell align="right">Birim Fiyat</TableCell>
                <TableCell align="right">Toplam</TableCell>
                <TableCell>Açıklama</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((line) => (
                <TableRow key={line.lineNumber}>
                  <TableCell>{line.lineNumber}</TableCell>
                  <TableCell>{line.itemCode}</TableCell>
                  <TableCell>{line.itemName}</TableCell>
                  <TableCell align="right">{line.quantity}</TableCell>
                  <TableCell>{line.unit}</TableCell>
                  <TableCell align="right">
                    {formatCurrency(line.unitPrice, header.currency)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(line.totalPrice, header.currency)}
                  </TableCell>
                  <TableCell>{line.description || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* System Information */}
      <Paper elevation={1} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ borderBottom: '1px solid #eee', pb: 1, mb: 2 }}>
          Sistem Bilgileri
        </Typography>
        
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Oluşturan
            </Typography>
            <Typography variant="body2">{header.createdBy}</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Oluşturma Tarihi
            </Typography>
            <Typography variant="body2">{formatDateTime(header.createdDate)}</Typography>
          </Box>
          {header.lastUpdatedBy && (
            <>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Son Güncelleyen
                </Typography>
                <Typography variant="body2">{header.lastUpdatedBy}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Son Güncelleme Tarihi
                </Typography>
                <Typography variant="body2">{formatDateTime(header.lastUpdatedDate!)}</Typography>
              </Box>
            </>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default OrderDetailPage;